import Swal from "sweetalert2";
import { t } from "i18next";
import { const_routes } from "../helpers/const_routes";
import { fetchToken } from "../helpers/fetch";
import { types } from "../types/types";
import { startLogout } from "./authActions";
import { debugLogActions, debugErrorActions } from "../helpers/debugHelper";

const handleError = (body, dispatch) => {
  if (body.code === "bak-error-001") {
    Swal.fire(t("titles.error"), `${t("alerts.validation.bak-error-001")} - ${body.method}`, "error");
  }
  if (body.code === "bak-aut-tok-inv-001" || body.code === "bak-aut-tok-inv-002") {
    Swal.fire(t("titles.error"), t("api.token.invalid_petition"), "error");
    dispatch(startLogout());
  }
};

const buildQuery = (filters = {}) => {
  const query = new URLSearchParams(filters).toString();
  return query ? `get?${query}` : "get";
};

export const startLoadingFlowModuleConfigs = (filters = {}) => {
  return async (dispatch) => {
    dispatch({ type: types.FLOW_MODULE_CONFIG_START_LOADING, payload: true });

    try {
      const query = new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
      ).toString();
      const suffix = query ? `get?${query}` : 'get';

      const [mgrResp, valResp, edResp] = await Promise.all([
        fetchToken(const_routes.SCHEMA, const_routes.VERSIONV1, const_routes.ROUTE_FLOW_MANAGER_CONFIG, suffix),
        fetchToken(const_routes.SCHEMA, const_routes.VERSIONV1, const_routes.ROUTE_FLOW_VALIDATOR_CONFIG, suffix),
        fetchToken(const_routes.SCHEMA, const_routes.VERSIONV1, const_routes.ROUTE_FLOW_EDITOR_CONFIG, suffix)
      ]);

      const [mgrBody, valBody, edBody] = await Promise.all([mgrResp.json(), valResp.json(), edResp.json()]);

      debugLogActions(":: flowModuleConfigActions, load all", { mgrBody, valBody, edBody });

      if (mgrBody.ok) {
        dispatch({ type: types.FLOW_MODULE_CONFIG_SET_MANAGERS, payload: mgrBody.flowManagerConfigs || [] });
      }
      if (valBody.ok) {
        dispatch({ type: types.FLOW_MODULE_CONFIG_SET_VALIDATORS, payload: valBody.flowValidatorConfigs || [] });
      }
      if (edBody.ok) {
        dispatch({ type: types.FLOW_MODULE_CONFIG_SET_EDITORS, payload: edBody.flowEditorConfigs || [] });
      }

      if (!mgrBody.ok) handleError(mgrBody, dispatch);
      if (!valBody.ok) handleError(valBody, dispatch);
      if (!edBody.ok) handleError(edBody, dispatch);
    } catch (error) {
      debugErrorActions(":: flowModuleConfigActions, startLoadingFlowModuleConfigs", error);
    } finally {
      dispatch({ type: types.FLOW_MODULE_CONFIG_START_LOADING, payload: false });
    }
  };
};

export const startRegisterFlowManagerConfig = (data) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_MANAGER_CONFIG,
        "add",
        data,
        "POST"
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch(startLoadingFlowModuleConfigs());
        return body.flowManagerConfig;
      }
      handleError(body, dispatch);
      return null;
    } catch (error) {
      debugErrorActions(":: flowModuleConfigActions, startRegisterFlowManagerConfig", error);
      return null;
    }
  };
};

export const startSyncFlowManagerConfigs = (data, filters = {}) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_MANAGER_CONFIG,
        "sync",
        data,
        "POST"
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch(startLoadingFlowModuleConfigs(filters));
        return body.flowManagerConfigs || [];
      }
      handleError(body, dispatch);
      if (body.msg) {
        Swal.fire(t("titles.error"), body.msg, "error");
      }
      return null;
    } catch (error) {
      debugErrorActions(":: flowModuleConfigActions, startSyncFlowManagerConfigs", error);
      return null;
    }
  };
};

export const startDeleteFlowManagerConfigsByUserModule = (userId, catalogmoduleId, filters = {}) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_MANAGER_CONFIG,
        "deleteByUserModule",
        { user_id: Number(userId), catalogmodule_id: Number(catalogmoduleId) },
        "DELETE"
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch(startLoadingFlowModuleConfigs(filters));
        return true;
      }
      handleError(body, dispatch);
      return false;
    } catch (error) {
      debugErrorActions(":: flowModuleConfigActions, startDeleteFlowManagerConfigsByUserModule", error);
      return false;
    }
  };
};

export const startRegisterFlowValidatorConfig = (data) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_VALIDATOR_CONFIG,
        "add",
        data,
        "POST"
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch(startLoadingFlowModuleConfigs());
        return body.flowValidatorConfig;
      }
      handleError(body, dispatch);
      return null;
    } catch (error) {
      debugErrorActions(":: flowModuleConfigActions, startRegisterFlowValidatorConfig", error);
      return null;
    }
  };
};

export const startRegisterFlowEditorConfig = (data) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_EDITOR_CONFIG,
        "add",
        data,
        "POST"
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch(startLoadingFlowModuleConfigs());
        return body.flowEditorConfig;
      }
      handleError(body, dispatch);
      return null;
    } catch (error) {
      debugErrorActions(":: flowModuleConfigActions, startRegisterFlowEditorConfig", error);
      return null;
    }
  };
};

export const startUpdateFlowManagerConfig = (id, data, filters = {}) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_MANAGER_CONFIG,
        `update/${id}`,
        data,
        'PUT'
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch(startLoadingFlowModuleConfigs(filters));
        return body.flowManagerConfig;
      }
      handleError(body, dispatch);
      return null;
    } catch (error) {
      debugErrorActions(':: flowModuleConfigActions, startUpdateFlowManagerConfig', error);
      return null;
    }
  };
};

export const startUpdateFlowValidatorConfig = (id, data, filters = {}) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_VALIDATOR_CONFIG,
        `update/${id}`,
        data,
        'PUT'
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch(startLoadingFlowModuleConfigs(filters));
        return body.flowValidatorConfig;
      }
      handleError(body, dispatch);
      return null;
    } catch (error) {
      debugErrorActions(':: flowModuleConfigActions, startUpdateFlowValidatorConfig', error);
      return null;
    }
  };
};

export const startUpdateFlowEditorConfig = (id, data, filters = {}) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_EDITOR_CONFIG,
        `update/${id}`,
        data,
        'PUT'
      );
      const body = await resp.json();
      if (body.ok) {
        dispatch(startLoadingFlowModuleConfigs(filters));
        return body.flowEditorConfig;
      }
      handleError(body, dispatch);
      return null;
    } catch (error) {
      debugErrorActions(':: flowModuleConfigActions, startUpdateFlowEditorConfig', error);
      return null;
    }
  };
};

export const startDeleteFlowManagerConfig = (id, filters = {}) => {
  return async (dispatch) => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_MANAGER_CONFIG,
      `delete/${id}`,
      {},
      "DELETE"
    );
    const body = await resp.json();
    if (body.ok) dispatch(startLoadingFlowModuleConfigs(filters));
    else handleError(body, dispatch);
    return body.ok;
  };
};

export const startDeleteFlowValidatorConfig = (id, filters = {}) => {
  return async (dispatch) => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_VALIDATOR_CONFIG,
      `delete/${id}`,
      {},
      "DELETE"
    );
    const body = await resp.json();
    if (body.ok) dispatch(startLoadingFlowModuleConfigs(filters));
    else handleError(body, dispatch);
    return body.ok;
  };
};

export const startDeleteFlowEditorConfig = (id, filters = {}) => {
  return async (dispatch) => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_FLOW_EDITOR_CONFIG,
      `delete/${id}`,
      {},
      "DELETE"
    );
    const body = await resp.json();
    if (body.ok) dispatch(startLoadingFlowModuleConfigs(filters));
    else handleError(body, dispatch);
    return body.ok;
  };
};

export const startLoadingFlowRoleUsers = (role, catalogmoduleId = '') => {
  return async () => {
    try {
      const params = { role };
      if (catalogmoduleId !== '' && catalogmoduleId !== null && catalogmoduleId !== undefined) {
        params.catalogmodule_id = catalogmoduleId;
      }
      const query = new URLSearchParams(params).toString();
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_FLOW_MODULE_CONFIG,
        `roleUsers?${query}`
      );
      const body = await resp.json();
      if (!body.ok) {
        debugErrorActions(':: flowModuleConfigActions, startLoadingFlowRoleUsers', body);
        return [];
      }
      return body.users || [];
    } catch (error) {
      debugErrorActions(':: flowModuleConfigActions, startLoadingFlowRoleUsers', error);
      return [];
    }
  };
};
